// Feature: Auto-hiding rules upon full validation & integrated eye-toggle
// Build ID: 1775638205 (Force Reload)

// React core and hooks for state and side effects
import React, { useState, useRef, useEffect } from "react";
// React Native UI primitives and animation API
import {
  View,
  Text,
  StyleSheet,
  Animated,
  Pressable,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
// Shared input component used as the base for this password field
import CommonInput from "./commonInput";
// Icon for the premium check indicator
import Icon from "./icon";
// Theme hook for light/dark adaptive colors
import { useTheme } from "../context/themeContext";

// Password validation utilities for rule checking and strength assessment
import {
  checkPasswordRules,
  isPasswordStrong,
  PASSWORD_RULE_LABELS,
} from "../utils/passwordRules";

/* ─────────────────────────────────────────
   Eye toggle glyph — crisp, professionally-drawn vector (Lucide-style).
   open=true  → eye          (password visible)
   open=false → eye-off      (password hidden)
   Rendered as multi-stroke SVG: inline <svg> on web, react-native-svg on native
   — round caps/joins at strokeWidth 2 for a clean, premium look.
───────────────────────────────────────── */
const EyeGlyph = ({ open, color, size = 19 }: { open: boolean; color: string; size?: number }) => {
  const common = {
    fill: 'none',
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  if (Platform.OS === 'web') {
    return (
      // @ts-ignore — web SVG
      <svg width={size} height={size} viewBox="0 0 24 24" {...common} style={{ display: 'block' }} aria-hidden="true">
        {open ? (
          <>
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </>
        ) : (
          <>
            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
            <line x1="2" y1="2" x2="22" y2="22" />
          </>
        )}
      </svg>
    );
  }

  // Native (react-native-svg)
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { default: Svg, Path, Circle, Line } = require('react-native-svg');
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...common}>
      {open ? (
        <>
          <Path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
          <Circle cx={12} cy={12} r={3} />
        </>
      ) : (
        <>
          <Path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
          <Path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
          <Path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
          <Line x1={2} y1={2} x2={22} y2={22} />
        </>
      )}
    </Svg>
  );
};

type PasswordInputProps = Readonly<{
  // Label displayed above the input field
  label: string;
  // Whether to show the premium required-field asterisk beside the label
  required?: boolean;
  // Placeholder text shown inside the input when empty
  placeholder: string;
  // Controlled value of the input field
  value: string;
  // Callback fired on every keystroke with the updated value
  onChangeText: (text: string) => void;
  // Optional error message to display below the input
  error?: string;
  // Whether the field has been touched by the user
  touched?: boolean;
  // Whether to show the password rule indicators below the input
  showRules?: boolean;
  // Optional blur event handler
  onBlur?: (e?: any) => void;
  // Callback fired when the keyboard submit button is pressed
  onSubmitEditing?: () => void;
  // Keyboard return key label type
  returnKeyType?: "done" | "go" | "next" | "send";
  // Controls validation strictness — signup requires strong password, login only requires non-empty
  mode?: "login" | "signup";
  /** Override the field's outer spacing (e.g. a denser form). Optional — the
   *  component's own layout is unchanged when it is omitted. */
  containerStyle?: StyleProp<ViewStyle>;
}>;

// Main PasswordInput component with eye toggle, shake animation, and inline rule indicators
export default function PasswordInput({
  label,
  required,
  placeholder,
  value,
  onChangeText,
  error,
  touched,
  showRules = false,
  onBlur,
  onSubmitEditing,
  // Default to 'done' if no return key type is specified
  returnKeyType = "done",
  // Default to signup mode for strict password validation
  mode = "signup",
  containerStyle,
}: PasswordInputProps) {
  // Track whether the input is currently focused
  const [isFocused, setIsFocused] = useState(false);
  // Track whether the user has interacted with the field at least once
  const [hasBeenInteracted, setHasBeenInteracted] = useState(false);
  // Toggle between masked and plain text password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Theme colors for light/dark adaptive surfaces, text, and indicators
  const { theme } = useTheme();
  const { colors } = theme;

  // Animated value used to drive the horizontal shake effect on error
  const shakeAnimation = useRef(new Animated.Value(0)).current;

  // Evaluate which password rules are currently passing
  const rules = checkPasswordRules(value);
  // Determine if all requirements are satisfied based on the current mode
  const allRulesMet =
    mode === "signup"
      ? isPasswordStrong(value)       // Signup requires strong password
      : value.trim().length > 0;      // Login only requires non-empty input
  const shouldShowRules = showRules && (isFocused || value.trim().length > 0) && !(mode === "signup" && allRulesMet);

  // Trigger shake animation whenever an error appears after interaction
  useEffect(() => {
    if ((touched || hasBeenInteracted) && error) {
      // Fire the shake sequence to draw attention to the error
      triggerShake();
    }
  }, [touched, hasBeenInteracted, error]);

  // Plays a left-right shake animation on the input to signal an error
  const triggerShake = () => {
    Animated.sequence([
      // Shift right
      Animated.timing(shakeAnimation, {
        toValue: 8,
        duration: 50,
        useNativeDriver: true,
      }),
      // Shift left
      Animated.timing(shakeAnimation, {
        toValue: -8,
        duration: 50,
        useNativeDriver: true,
      }),
      // Return to center
      Animated.timing(shakeAnimation, {
        toValue: 0,
        duration: 50,
        useNativeDriver: true,
      }),
    ]).start();
  };

  return (
    // Outer container wrapping the entire password field and rule indicators
    <View style={[styles.container, containerStyle]}>
      {/* Animated wrapper applies horizontal shake on validation error */}
      <Animated.View style={{ transform: [{ translateX: shakeAnimation }] }}>
        <CommonInput
          // The OUTER container owns this field's bottom gap (see
          // styles.container). Without this, the inner CommonInput's own
          // marginBottom stacked on top of it and a caller overriding
          // `containerStyle` got its value PLUS 18 — which is exactly how the
          // signup form ended up with 26px under its password fields while
          // every other field had 8.
          containerStyle={styles.innerField}
          label={label}
          required={required}
          placeholder={placeholder}
          value={value}
          onChangeText={onChangeText}
          // Toggle between secure and plain text entry based on visibility state
          secureTextEntry={!showPassword}
          // Keep copy/cut/paste blocked even while the value is revealed by the eye toggle
          disableCopyPaste
          // Show error state if the field has been touched or interacted with
          touched={touched || hasBeenInteracted}
          // Only pass the error message if the field has been touched or interacted with
          error={touched || hasBeenInteracted ? error : undefined}
          // In login mode always return valid; in signup mode check all rules
          isValid={() => (mode === "login" ? true : allRulesMet)}
          inputProps={{
            // Mark field as focused when the user taps into it
            onFocus: () => setIsFocused(true),
            onBlur: (e: any) => {
              // Mark field as unfocused
              setIsFocused(false);
              // Flag that the user has interacted with the field
              setHasBeenInteracted(true);
              // Forward blur event to parent if provided
              onBlur?.(e);
            },
            // Forward keyboard submit handler for form navigation
            onSubmitEditing,
            // Set the return key label on the keyboard
            // Set the return key label on the keyboard
            returnKeyType,
            // Improve emoji rendering on Android by switching to visible-password when unmasked
            keyboardType: Platform.OS === "android" && showPassword ? "visible-password" : "default",
          }}
          // Eye toggle button rendered as the right side icon of the input
          rightIcon={
            <Pressable
              onPress={() => setShowPassword(!showPassword)}
              hitSlop={6}
              accessibilityRole="button"
              accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
              style={({ pressed }: any) => [
                styles.eyeBtn,
                { transform: [{ scale: pressed ? 0.9 : 1 }] },
              ]}
            >
              <EyeGlyph
                open={showPassword}
                color={showPassword ? colors.accent : colors.ink4}
              />
            </Pressable>
          }
        />
      </Animated.View>

      {/* Show rules while focused, or when user has started typing */}
      {shouldShowRules && (
        <View
          style={[
            styles.rulesCard,
            { backgroundColor: colors.paper2, borderColor: colors.line },
          ]}
        >
          {/* Render each password rule as a premium check-circle + label */}
          {PASSWORD_RULE_LABELS.map(({ key, label }) => {
            // Check if this specific rule is currently passing
            const passed = rules[key];
            return (
              // Individual rule row with check-circle and label
              <View key={key} style={styles.ruleItem}>
                <View
                  style={[
                    styles.ruleCheck,
                    passed
                      ? { backgroundColor: colors.success, borderColor: colors.success }
                      : { backgroundColor: colors.card, borderColor: colors.lineStrong },
                  ]}
                >
                  {passed && <Icon name="check" size={11} color={colors.white} />}
                </View>

                <Text
                  style={[
                    styles.ruleText,
                    {
                      color: passed ? colors.ink : colors.ink3,
                      fontWeight: passed ? "600" : "500",
                    },
                  ]}
                >
                  {label}
                </Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

// StyleSheet for all visual elements in the PasswordInput component
const styles = StyleSheet.create({
  // Full-width wrapper for the entire component
  container: {
    width: "100%",
    // The field's bottom gap lives HERE, not on the inner CommonInput, so a
    // caller passing `containerStyle` can actually change it. 18 is the value
    // the inner input used to contribute, so every existing caller (login,
    // reset password, invite-accept) renders exactly as before.
    marginBottom: 18,
  },

  // Zero, because styles.container above is the single owner of the gap.
  innerField: {
    marginBottom: 0,
  },
  // Clean eye-toggle tap target — no background; just the glyph with a subtle
  // press response.
  eyeBtn: {
    width: 34,
    height: 34,
    justifyContent: "center",
    alignItems: "center",
    ...(Platform.OS === "web" ? ({ cursor: "pointer", transition: "transform 120ms ease" } as any) : {}),
  },
  // Premium card wrapping the password rule indicators.
  // Negative top margin offsets CommonInput's 18px marginBottom so the card sits
  // close to its password field; the bottom margin gives clear separation from
  // the next field (e.g. Confirm Password).
  rulesCard: {
    // Was `-6` when the inner input still contributed an 18px gap below itself
    // (18 - 6 = 12 of visible space). That gap now lives on the outer
    // container, so the same 12px is expressed directly.
    marginTop: 12,
    // The outer container owns the space BELOW the field, rules or no rules.
    marginBottom: 0,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 10,
  },

  // Row layout for each individual rule item
  ruleItem: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },

  // Premium check-circle indicator for each rule
  ruleCheck: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1.5,
    marginRight: 10,
    alignItems: "center",
    justifyContent: "center",
  },

  // Text style for rule labels
  ruleText: {
    fontSize: 12.5,
    letterSpacing: 0.1,
  },

});